import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Megaphone, CheckCircle, Warning, ArrowSquareOut, MagnifyingGlass, PencilSimple, Link as LinkIcon, Sparkle, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import PhotoLightbox from "@/components/PhotoLightbox";
import AlendraAI from "@/components/AlendraAI";

const TABS = [
  { v: "missing", label: "Por publicar", color: "#E0000F", Icon: Megaphone },
  { v: "untouched", label: "Ningún vendedor publicó", color: "#7E57C2", Icon: Sparkle },
  { v: "expired", label: "Republicar (restock)", color: "#FF8A00", Icon: Warning },
  { v: "published", label: "Publicadas por mí", color: "#00873D", Icon: CheckCircle },
];

const isUrl = (s) => /^https?:\/\/[^\s]+$/i.test(String(s || "").trim());

export default function Publications() {
  const [data, setData] = useState({ items: [], totals: { total: 0, published: 0, expired: 0, missing: 0 } });
  const [tab, setTab] = useState("missing");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {item, mode: 'publish'|'edit'}
  const [draftUrl, setDraftUrl] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [lb, setLb] = useState({ open: false, photos: [], title: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/publications/products");
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar publicaciones");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const statusOf = (it) => {
    if (!it.publication) return "missing";
    return it.publication.expired ? "expired" : "published";
  };
  // True when NO OTHER vendor of the team has published this product
  // (regardless of whether the current user has). Helps supervisors spot
  // products the team is ignoring.
  const isUntouched = (it) => (it.team_published_count || 0) === 0;

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return data.items
      .filter((i) => tab === "untouched" ? isUntouched(i) : statusOf(i) === tab)
      .filter((i) => !t || (i.product_name || "").toLowerCase().includes(t)
        || (i.brand || "").toLowerCase().includes(t)
        || (i.category || "").toLowerCase().includes(t));
  }, [data, tab, search]);

  const startPublish = (it) => {
    setEditing({ item: it, mode: it.publication ? "edit" : "publish" });
    setDraftUrl(it.publication?.marketplace_url || "");
    setDraftNotes(it.publication?.notes || "");
  };
  const cancel = () => { setEditing(null); setDraftUrl(""); setDraftNotes(""); };

  const save = async () => {
    const url = draftUrl.trim();
    if (url && !isUrl(url)) { toast.error("Si pones un link, debe empezar con http:// o https://"); return; }
    setSaving(true);
    try {
      if (editing.item.publication) {
        await api.put(`/publications/${editing.item.publication.id}`, {
          marketplace_url: url,
          notes: draftNotes,
        });
      } else {
        await api.post("/publications", {
          product_id: editing.item.product_id,
          marketplace_url: url,
          notes: draftNotes,
        });
      }
      toast.success(`"${editing.item.product_name}" actualizado`);
      cancel();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  // Quick toggle: marca como publicado sin pedir link.
  const togglePublished = async (it) => {
    try {
      if (it.publication && !it.publication.expired) {
        // already published → unmark
        if (!window.confirm(`¿Marcar como NO publicado "${it.product_name}"?`)) return;
        await api.delete(`/publications/${it.publication.id}`);
        toast.success("Marcado como no publicado");
      } else {
        await api.post("/publications", { product_id: it.product_id, marketplace_url: "", notes: "" });
        toast.success(`"${it.product_name}" marcado como publicado ✓`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const openLightbox = (it) => {
    const photos = (it.photos || []).filter(Boolean);
    if (photos.length === 0) return;
    setLb({ open: true, photos, title: it.product_name });
  };

  const fmtPrice = (n, c) => `${c === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };

  const t = data.totals;
  const coveragePct = t.total ? Math.round((t.published / t.total) * 100) : 0;

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="publications-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">MARKETPLACE</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Mis Publicaciones</h1>
          <p className="text-sm text-zinc-500 mt-1">Registra cada producto que subiste a Facebook Marketplace, MercadoLibre, etc.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Buscar nombre / marca / categoría…" value={search} onChange={(e)=>setSearch(e.target.value)} className="pl-9" data-testid="pub-search" />
        </div>
      </header>

      {/* KPI bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pub-kpis">
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Publicables</div>
          <div className="numeric text-2xl font-bold mt-1">{t.total}</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Por publicar</div>
          <div className="numeric text-2xl font-bold mt-1 text-[#E0000F]">{t.missing}</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Restock pendiente</div>
          <div className="numeric text-2xl font-bold mt-1 text-[#FF8A00]">{t.expired}</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Cobertura</div>
          <div className="numeric text-2xl font-bold mt-1 text-[#00873D]">{coveragePct}%</div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{t.published} publicadas</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {TABS.map((cfg) => {
          const count = cfg.v === "missing" ? t.missing
            : cfg.v === "expired" ? t.expired
            : cfg.v === "untouched" ? (t.untouched || 0)
            : t.published;
          return (
            <button
              key={cfg.v}
              type="button"
              onClick={() => setTab(cfg.v)}
              data-testid={`pub-tab-${cfg.v}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-sm border text-sm font-semibold transition ${
                tab === cfg.v ? "" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"
              }`}
              style={tab === cfg.v ? { background: `${cfg.color}15`, borderColor: `${cfg.color}55`, color: cfg.color } : {}}
            >
              <cfg.Icon size={16} weight="bold" />
              {cfg.label}
              <span className="font-mono-data text-xs px-2 py-0.5 bg-white rounded">{count}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div className="border border-dashed border-zinc-300 rounded p-10 text-center text-zinc-400 dotted-bg">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded p-10 text-center text-zinc-400 dotted-bg">
          {tab === "missing" && "¡No tienes pendientes! 🎉 Cada producto con foto ya está publicado."}
          {tab === "untouched" && "Excelente cobertura — todos los productos disponibles ya tienen al menos un vendedor publicándolos. 🚀"}
          {tab === "expired" && "Sin restocks pendientes."}
          {tab === "published" && "Aún no has publicado nada en marketplace."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pub-grid">
          {filtered.map((it) => {
            const st = statusOf(it);
            const cfg = TABS.find((x) => x.v === st) || TABS[0];
            const cover = (it.photos || [])[0];
            return (
              <article key={it.product_id} className="border border-zinc-200 bg-white flex flex-col" data-testid={`pub-card-${it.product_id}`}>
                <button type="button" onClick={() => openLightbox(it)} className="aspect-video bg-zinc-50 overflow-hidden flex items-center justify-center relative group cursor-zoom-in border-b border-zinc-100">
                  {cover ? <img src={cover} alt={it.product_name} className="max-w-full max-h-full object-contain transition-transform group-hover:scale-[1.02]" /> : <span className="text-xs text-zinc-400">sin foto</span>}
                  {(it.photos || []).length > 1 && (
                    <span className="absolute bottom-1 right-1 px-1.5 py-0.5 text-[10px] font-mono-data bg-black/70 text-white rounded">+{it.photos.length - 1}</span>
                  )}
                  <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-sm flex items-center gap-1" style={{ background: `${cfg.color}`, color: "white" }}>
                    <cfg.Icon size={11} weight="bold"/>
                    {cfg.label}
                  </span>
                </button>
                <div className="p-3 space-y-2 flex-1 flex flex-col">
                  <div>
                    <h3 className="font-semibold text-sm leading-tight" title={it.product_name}>{it.product_name}</h3>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {[it.brand, it.category].filter(Boolean).join(" · ") || "—"}
                      <span className="mx-1 text-zinc-300">·</span>
                      <span className="font-mono-data">{it.stock} en stock</span>
                    </div>
                  </div>

                  {it.characteristics && (
                    <p className="text-[11px] text-zinc-600 leading-relaxed line-clamp-3 whitespace-pre-line" title={it.characteristics} data-testid={`pub-desc-${it.product_id}`}>
                      {it.characteristics}
                    </p>
                  )}

                  {/* Price tiers */}
                  <div className="grid grid-cols-2 gap-1 text-[10px] font-mono-data text-zinc-500">
                    <div><span className="uppercase">Reventa</span><div className="numeric text-zinc-800">{fmtPrice(it.reseller_price, it.sale_currency)}</div></div>
                    <div><span className="uppercase">Tienda</span><div className="numeric text-zinc-800">{fmtPrice(it.store_price, it.sale_currency)}</div></div>
                  </div>

                  {/* Team coverage badge */}
                  {(it.team_published_count || 0) > 0 ? (
                    <div className="flex items-center gap-1.5 text-[10px] text-[#7E57C2]" title={(it.team_publishers || []).map((u) => u.user_name || u.user_email).join(", ")} data-testid={`team-cov-${it.product_id}`}>
                      <Users size={11} weight="fill" />
                      <span>Publicado por {it.team_published_count} {it.team_published_count === 1 ? "vendedor más" : "vendedores más"}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-[10px] text-[#7E57C2] font-semibold" data-testid={`team-cov-${it.product_id}`}>
                      <Sparkle size={11} weight="fill" />
                      <span>Ningún vendedor lo ha publicado</span>
                    </div>
                  )}

                  {it.publication && (
                    <div className="mt-1 p-2 rounded-sm border" style={{ borderColor: `${cfg.color}55`, background: `${cfg.color}08` }}>
                      <div className="text-[10px] text-zinc-500">Marcado como publicado · {fmtDate(it.publication.published_at)}</div>
                      {it.publication.marketplace_url && (
                        <a href={it.publication.marketplace_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#002FA7] inline-flex items-center gap-1 hover:underline break-all mt-0.5">
                          <ArrowSquareOut size={11}/> {it.publication.marketplace_url}
                        </a>
                      )}
                      {st === "expired" && it.last_restock_at && (
                        <div className="text-[10px] text-[#FF8A00] mt-0.5 font-semibold">⚠ Restock el {fmtDate(it.last_restock_at)} — verifica si tu post sigue activo.</div>
                      )}
                      {it.publication.notes && <div className="text-[10px] text-zinc-600 mt-1 italic">&ldquo;{it.publication.notes}&rdquo;</div>}
                    </div>
                  )}

                  <div className="mt-auto pt-2 flex gap-2 items-center">
                    {/* Primary action: simple checkbox-style toggle */}
                    <label
                      className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer text-xs font-semibold transition select-none ${
                        st === "published"
                          ? "bg-[#00873D]/10 border-[#00873D]/40 text-[#00873D]"
                          : st === "expired"
                            ? "bg-[#FF8A00]/10 border-[#FF8A00]/40 text-[#FF8A00] hover:bg-[#FF8A00]/15"
                            : "bg-white border-zinc-300 text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7]"
                      }`}
                      data-testid={`pub-toggle-${it.product_id}`}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-[#00873D] cursor-pointer"
                        checked={st === "published"}
                        onChange={() => togglePublished(it)}
                      />
                      {st === "missing" && <><Megaphone size={13} weight="bold"/> Marcar como publicado</>}
                      {st === "expired" && <><Warning size={13} weight="bold"/> Republicar (restock)</>}
                      {st === "published" && <><CheckCircle size={13} weight="fill"/> Publicado</>}
                    </label>
                    {/* Alendra AI: botón principal para generar publicación */}
                    <Button
                      size="sm"
                      onClick={() => startPublish(it)}
                      title="Generar publicación con Alendra AI"
                      className="gap-1.5 text-xs bg-gradient-to-br from-[#002FA7] to-[#003ed4] text-white hover:from-[#003ed4] hover:to-[#0050ff] shadow-sm hover:shadow-md transition px-3"
                      data-testid={`pub-alendra-${it.product_id}`}
                    >
                      <Sparkle size={14} weight="fill"/>
                      <span className="hidden sm:inline">Alendra AI</span>
                    </Button>
                    {/* Editor manual (link / notas) — más discreto */}
                    <Button size="sm" variant="outline" onClick={() => startPublish(it)} title="Agregar link o notas (opcional)" className="gap-1 text-xs" data-testid={`pub-action-${it.product_id}`}>
                      <PencilSimple size={13}/>
                    </Button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Publish / edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={cancel}>
          <div className="bg-white rounded-md max-w-3xl w-full p-6 space-y-4 my-8" onClick={(e)=>e.stopPropagation()} data-testid="pub-editor">
            <div>
              <h2 className="font-heading text-2xl font-bold">{editing.item.product_name}</h2>
              <p className="text-xs text-zinc-500">{[editing.item.brand, editing.item.category].filter(Boolean).join(" · ") || "—"} · {editing.item.stock} en stock</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Columna izquierda: datos de la publicación */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="pub-url" className="text-xs uppercase tracking-wider">Link del marketplace (opcional)</Label>
                  <div className="relative mt-1">
                    <LinkIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400"/>
                    <Input
                      id="pub-url"
                      value={draftUrl}
                      onChange={(e)=>setDraftUrl(e.target.value)}
                      placeholder="https://facebook.com/marketplace/item/..."
                      className="pl-8 text-xs"
                      data-testid="pub-url-input"
                      autoFocus
                    />
                  </div>
                  <p className="text-[10px] text-zinc-400 mt-1">Puedes guardar el link del post para abrirlo más rápido luego.</p>
                </div>
                <div>
                  <Label htmlFor="pub-notes" className="text-xs uppercase tracking-wider">Notas (opcional)</Label>
                  <Textarea
                    id="pub-notes"
                    value={draftNotes}
                    onChange={(e)=>setDraftNotes(e.target.value)}
                    placeholder="Ej: anuncié con precio $4,500, oferta hasta el viernes…"
                    className="mt-1 text-xs"
                    rows={6}
                    data-testid="pub-notes-input"
                  />
                </div>
              </div>

              {/* Columna derecha: Alendra AI para generar texto de la publicación */}
              <div>
                <AlendraAI
                  productId={editing.item.product_id}
                  product={editing.item}
                  hideDescription
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
              <Button type="button" variant="outline" onClick={cancel} disabled={saving}>Cancelar</Button>
              <Button type="button" onClick={save} disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="pub-save">
                {saving ? "Guardando…" : editing.mode === "edit" ? "Actualizar" : "Marcar publicado"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <PhotoLightbox open={lb.open} onClose={()=>setLb((s)=>({...s, open:false}))} photos={lb.photos} title={lb.title} filenameBase={lb.title} />
    </div>
  );
}
